import { FC } from 'react';
import { TbUsers } from 'react-icons/tb';

import { useResourceActionAccess } from '@/hooks/useResourceActionAccess';
import {
    ActionsDropdownButton,
    ResourceHeader,
    ResourceHeaderStatusActions,
    ResourceHeaderTitle,
    TeamsActionsDropdown,
} from '@/shared';
import { useResourceRoutePreview } from '@/shared/components/resource-route-preview/context';
import ResourcePreviewField from '@/shared/components/resource-route-preview/ResourcePreviewField';
import { useTeamStore } from '@/stores';
import { Team, WorkspaceSubjectType } from '@/types';

interface HeaderProps {
    team: Team;
}

const Header: FC<HeaderProps> = ({ team }): JSX.Element => {
    const preview = useResourceRoutePreview();
    const { toggleTeamArchive, toggleTeamFavorite, toggleTeamLock, updateTeam } = useTeamStore();
    const updateAccess = useResourceActionAccess('teams', 'update', team?.id);

    const handleToggleFavorite = async () => {
        toggleTeamFavorite(team?.id, !team?.favorited);
    };

    const handleToggleLock = async () => {
        toggleTeamLock(team?.id, !team?.locked);
    };

    const handleToggleArchive = async () => {
        toggleTeamArchive(team?.id, !team?.archived);
    };

    return (
        <ResourceHeader
            style={{ padding: '1.5rem 0', paddingTop: '0.5rem' }}
            breadcrumbs={[
                {
                    icon: <TbUsers />,
                    label: 'Teams',
                    to: '/settings/workspace/permissions/teams',
                },
                {
                    content: (
                        <ResourcePreviewField field='name' resourceType='teams' resourceId={team.id}>
                            <ResourceHeaderTitle
                                editable={{
                                    ariaLabel: 'Rename team',
                                    disabled: Boolean(preview) || team?.locked || updateAccess.disabled,
                                    onSave: (name) => updateTeam(team.id, { name }),
                                }}
                                title={team?.name}
                            />
                        </ResourcePreviewField>
                    ),
                },
            ]}
            leftActions={
                preview ? (
                    <ResourcePreviewField field='visibility' resourceType='teams' resourceId={team.id}>
                        {team.visibility}
                    </ResourcePreviewField>
                ) : (
                    <ResourceHeaderStatusActions
                        favorite={{
                            active: team?.favorited,
                            onClick: handleToggleFavorite,
                        }}
                        tags={{
                            disabled: team?.locked || updateAccess.disabled,
                            resourceLabel: team?.name ?? 'Team',
                            subject: {
                                subjectId: team?.id,
                                subjectLabel: team?.name,
                                subjectType: WorkspaceSubjectType.TEAM,
                            },
                        }}
                        lock={{ active: team?.locked, onClick: handleToggleLock }}
                        archive={{ active: team?.archived, onClick: handleToggleArchive }}
                    />
                )
            }
            rightActions={
                preview ? null : (
                    <TeamsActionsDropdown team={team}>
                        <ActionsDropdownButton />
                    </TeamsActionsDropdown>
                )
            }
        />
    );
};

export default Header;
